import _ from 'lodash'

export const TwoCase = {
	share: 'share', // In the case of e-tags (one e-tag for two cells)
	separate: 'separate', // In the case of products (two different products for two cells)
	single: 'single', // Consider 2 as a single cell
}

export enum PLANOGRAM_LAYOUTS {
	BASIC = '18',
	FIVETOP = '20',
	FIVEALL = '30',
}

/** Sentinel cell_index for the extra slot in planogram_cells / inventory_plan_planogram_cells. */
export const EXTRA_CELL_INDEX = 1000

export const standardLayouts = {
	[PLANOGRAM_LAYOUTS.BASIC]: '111,111,111,111,111,111',
	[PLANOGRAM_LAYOUTS.FIVETOP]: '11111,111,111,111,111,111',
	[PLANOGRAM_LAYOUTS.FIVEALL]: '11111,11111,11111,11111,11111,11111',
}

export type SiteInventoryCell = {
	id?: number
	site_inventory: any
	position?: 'left' | 'right'
	cell_index?: number
}

export type Cell = {
	content?: number
	cell_index: number
	position?: 'left' | 'right'
}

export type GridCell = Cell | SiteInventoryCell

export type Grid = GridCell[][]

class Planogram {
	static getLayout(layout): string {
		if (!layout) {
			return standardLayouts[PLANOGRAM_LAYOUTS.BASIC]
		}
		return standardLayouts[layout] ?? layout
	}

	static isValidLayout(value: string) {
		const regex = /^[12,]+$/
		const validEnums: string[] = Object.values(PLANOGRAM_LAYOUTS)
		if (!regex.test(value) && !validEnums.includes(value)) {
			throw new Error(
				`Layout can only contain character "1", "2", and "," or be one of the predefined layouts: ${validEnums.join(', ')}`
			)
		}
	}

	/**
	 * Converts a one-dimensional list of cells ([c1, c2, c3, c4, ...]) into a grid of cells ([[c1, c2], [c3, c4], ...])
	 * based on a layout string in the form "111,211,...".
	 *
	 * The layout string is interpreted as follows:
	 * - "1" adds a cell to the current row.
	 * - "," starts a new row.
	 * - "2" can mean two different things depending on the `twoCase` parameter:
	 *   - If `twoCase` is 'share', the cell information is shared between two columns (e.g., e-tags).
	 *   - If `twoCase` is 'separate', the cell information is split into two columns (e.g., products).
	 *
	 * @param layout - The layout string to interpret.
	 * @param cells - A list of cells to be organized into a grid.
	 * @param twoCase - Determines how "2" is handled ('share' or 'separate'). Default is `TwoCase.separate`.
	 * @param addExtra - Whether to add extra cells if necessary. Default is `false` unless one extra cell then layout count
	 * @returns A grid of cells organized according to the layout.
	 */
	static parseLayout(layout: string, cells: string[] | any[], twoCase = TwoCase.separate, addExtra = undefined): Grid {
		layout = this.getLayout(layout)

		const layoutCells: Grid = []

		// Check if the cell count is greater than one, and if so addExtra if not set
		addExtra = addExtra ?? (cells.length == this.countCells(layout) + 1)

		let cell_index = 0

		// Convert layout string to 2D array
		// e.g. '111,111,111' => [[1,1,1], [1,1,1], [1,1,1]]
		const rows = _.map(layout.split(','), (row) => _.map(row.split(''), (cell) => parseInt(cell)))

		// Convert string[] to array of objects with { content: string }
		cells = _.map(cells, (c) => _.isObject(c) ? c : { content: c })

		rows.forEach((row, rowIndex) => {
			layoutCells[rowIndex] = []
			row.forEach((column, columnIndex) => {
				const cell = cells.shift() || {}
				const nextCell = cells[0] || {}

				cell.cell_index = cell_index
				nextCell.cell_index = cell_index + 1

				if (column == 1 || (column == 2 && twoCase == TwoCase.single)) {
					layoutCells[rowIndex].push(cell)
				} else if (column == 2 && twoCase == TwoCase.share) {
					layoutCells[rowIndex].push({ ...cell, position: 'left' })
					layoutCells[rowIndex].push({ ..._.clone(cell), cell_index: nextCell.cell_index, position: 'right' })
					columnIndex += 1
					cell_index += 1
				} else if (column == 2 && twoCase == TwoCase.separate) {
					cell.position = 'left'
					nextCell.position = 'right'
					layoutCells[rowIndex].push(cell)
					layoutCells[rowIndex].push(nextCell)
					cell_index += 1
					cells.shift()
				}

				cell_index += 1
			})
		})

		if (addExtra) {
			if (cells.length) {
				// TODO: maybe have read-only planograms, and when more than one cell left in list, throw an error
				layoutCells.push([{ ...cells[0], cell_index }])
			} else {
				layoutCells.push([{ cell_index }])
			}
		}
		return layoutCells
	}

	static countCells(layout, etagsOnly = false): number {
		if (Object.values(PLANOGRAM_LAYOUTS).includes(layout)) {
			return Number(layout)
		}

		return layout.split(',').join('').split('').reduce((acc, val) => acc + (etagsOnly ? 1 : parseInt(val)), 0)
	}

	/**
	 * Helper function to calculate row and column position for a given cell_index
	 * based on the layout string (e.g., "211,1111,222,111")
	 *
	 * The layout format works as follows:
	 * - Commas separate rows
	 * - Each digit represents the width/number of grid columns a cell occupies
	 * - Cells are numbered sequentially from 0
	 * - _column represents the starting visual column position in the grid
	 *
	 * Example: "211,1111"
	 *   Row 0: "211"
	 *     - Cell 0: width 2, starts at column 0
	 *     - Cell 1: width 1, starts at column 2
	 *     - Cell 2: width 1, starts at column 3
	 *   Row 1: "1111"
	 *     - Cell 3: width 1, starts at column 0
	 *     - Cell 4: width 1, starts at column 1
	 *     - Cell 5: width 1, starts at column 2
	 *     - Cell 6: width 1, starts at column 3
	 */
	static calculateCellPosition(layout: string, cellIndex: number): { row: number; column: number } | null {
		if (!layout || cellIndex < 0) {
			return null
		}

		const rows = layout.split(',')
		let currentIndex = 0

		for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
			const row = rows[rowIdx]
			const digits = row.split('').map(d => parseInt(d, 10))
			const cellsInRow = digits.reduce((sum, digit) => sum + digit, 0)

			if (cellIndex < currentIndex + cellsInRow) {
				// Cell is in this row, calculate visual column position
				let columnPosition = 0
				let indexInRow = currentIndex

				for (const cellWidth of digits) {
					if (cellIndex < indexInRow + cellWidth) {
						// Found the section containing this cell
						// The column is the base position plus offset within this section
						const offsetWithinSection = cellIndex - indexInRow
						return { row: rowIdx, column: columnPosition + offsetWithinSection }
					}
					columnPosition += cellWidth
					indexInRow += cellWidth
				}
			}

			currentIndex += cellsInRow
		}

		return null
	}
}

/**
 * Row/column → slot label (A1, A2, … B1, …) matching Platform planogram inventory UI.
 */
export function indexToSlotLabel(rowIndex: number, colIndex: number): string {
	let result = ''
	let index = rowIndex
	const iterations = Math.floor(index / 26) + 1
	for (let t = 0; t < iterations; t++) {
		result = String.fromCharCode((index % 26) + 65) + result
		index = Math.floor(index / 26) - 1
	}
	return `${result}${colIndex + 1}`
}

export default Planogram
